<template>
  <section>
    <div class="popups">
      <b-sidebar
        v-model="showFilters"
        right
        shadow
        backdrop
        bg-variant="white"
        no-header
      >
        <template>
          <div class="container sidebar-header">
            <b-row>
              <div class="col">
                <b-button
                  variant="primary"
                  @click="
                    applyFilters();
                    loadItems();
                    showFilters = false;
                  "
                  >Apply</b-button
                >
              </div>

              <div class="col-auto text-right">
                <b-button
                  type="reset"
                  variant="outline-danger"
                  @click="resetFormFilters()"
                >
                  Reset
                </b-button>
                <b-button
                  variant="outline-secondary"
                  class="ml-1"
                  @click="
                    showFilters = false;
                    formFilters = { ...itemsFilters };
                  "
                >
                  Cancel
                </b-button>
              </div>
            </b-row>
          </div>

          <div class="px-3 py-2">
            <b-form-group label="Status:">
              <b-v-select
                v-model="formFilters.status"
                :searchable="true"
                :options="statusFilter"
              />
            </b-form-group>
            <b-form-group label="Source:">
              <b-v-select
                v-model="formFilters.source"
                :searchable="true"
                :options="sourceFilter"
              />
            </b-form-group>
            <b-form-group label="Country:">
              <b-form-input v-model="formFilters.country" />
            </b-form-group>
            <b-form-group label="Charge Class Code:">
              <b-form-input v-model="formFilters.chargeClassCode" />
            </b-form-group>
            <b-form-group label="Charge Description Code:">
              <b-form-input v-model="formFilters.chargeDescriptionCode" />
            </b-form-group>
            <b-form-group label="Charge Description:">
              <b-form-input v-model="formFilters.chargeDescription" />
            </b-form-group>
            <b-form-group label="Type:">
              <b-v-select
                v-model="formFilters.type"
                :searchable="true"
                :options="typeFilter"
              />
            </b-form-group>
          </div>
        </template>
      </b-sidebar>
    </div>

    <div class="header">
      <div class="header-body">
        <div class="row align-items-end">
          <div class="col">
            <h1 class="header-title">
              Config: Carrier Charge Classifications
            </h1>
            <ActiveFiltersCard :itemsFilters="itemsFilters" :resetFilterWithKey="resetFilterWithKey" :loadItems="loadItems">
              <template v-slot:filterVal(source)="data">
                <span v-if="data.item">{{ data.item }}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:filterVal(type)="data">
                <span v-if="data.item">{{ data.item }}</span>
                <span v-else>-</span>
              </template>
            </ActiveFiltersCard>
          </div>
          <div class="col-auto">
            <b-button variant="primary" @click="showFilters = !showFilters">
              <i class="fa fa-filter mr-2"></i>
              Filters
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <TableListing
      class="mb-0"
      :loading-page="loadingItems"
      :failed-loading="failedLoadItems"
      head-variant="dark"
      :items.sync="items"
      :fields.sync="fields"
      :pagination.sync="pagination"
      :options.sync="tableOptions"
      v-on:pagination-changed="loadItems"
    >
      <template v-slot:cell(createDateTimeUtc)="data">
        <span
          v-if="
            data.item.createDateTimeUtc &&
              !data.item.createDateTimeUtc.endsWith('Z')
          "
          >{{
            (data.item.createDateTimeUtc + 'Z') | moment('MMM Do YY, h:mm a')
          }}</span
        >
        <span v-else-if="data.item.createDateTimeUtc">{{
          data.item.createDateTimeUtc | moment('MMM Do YY, h:mm a')
        }}</span>
      </template>
      <template v-slot:cell(source)="data">
        <span class="badge badge-primary" v-if="data.item.source == 'UPSDirect'">UPS: General Invoice</span>
        <span class="badge badge-info" v-else-if="data.item.source == 'UPSTaxesAndDuties'">UPS: Taxes & Duties</span>
        <span class="badge badge-warning" v-else-if="data.item.source == 'DHLDirect'">DHL: Auto-Invoice</span>
        <span class="badge badge-success" v-else-if="data.item.source == 'LandmarkMercury'">Landmark: Mercury</span>
        <span class="badge badge-secondary" v-else>{{ data.item.source }}</span>
      </template>
      <template v-slot:cell(type)="data">
        <span class="badge badge-primary" v-if="data.item.type == 'FreightCharge'">Freight Charge</span>
        <span class="badge badge-info" v-else-if="data.item.type == 'FuelSurcharge'">Fuel Surcharge</span>
        <span class="badge badge-warning" v-else-if="data.item.type == 'GeneralSurcharge'">General Surcharge</span>
        <span class="badge badge-dark" v-else-if="data.item.type == 'Unknown'">Unknown</span>
        <span class="badge badge-secondary" v-else>{{ data.item.type }}</span>
      </template>
      <template v-slot:cell(chargeDescription)="data">
        <span v-if="data.item.chargeDescription">{{ data.item.chargeDescription }}</span>
        <span v-else class="text-muted">-</span>
      </template>
      <template v-slot:cell(actions)="data">
        <router-link
          :to="{ name: 'Billing.CarrierInvoiceChargeConfigurations.Edit', params: { id: data.item.id } }"
          class="btn btn-sm btn-outline-primary"
          title="Edit Configuration"
        >
          <i class="fa fa-edit"></i>
          Edit
        </router-link>
      </template>
    </TableListing>
  </section>
</template>

<script>
import ActiveFiltersCard from '@/views/components/ActiveFiltersCard.vue';
import TableListing from '@/views/components/TableListing.vue';
import { crudMixinGen, myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

export default {
  name: 'Billing.CarrierInvoiceChargeConfigurations.Index',
  mixins: [crudMixinGen('billing/carrierInvoiceChargeConfigurations'), myCommonDataMixin],
  components: {
    ActiveFiltersCard,
    TableListing
  },
  computed: {
  },

  data: () => ({
    statusFilter: [
      {value: null, text: 'All Statuses'},
      {value: 'Ready', text: 'Ready'},
      {value: 'ToReview', text: 'Needs Review'}
    ],
    sourceFilter: [
      {value: 'UPSDirect', text: 'UPS Direct'},
      //{value: 'UPSTaxesAndDuties', text: 'UPS Taxes & Duties'},
      {value: 'DHLDirect', text: 'DHL Direct'},
      {value: 'LandmarkMercury', text: 'Landmark Mercury'}
    ],
    typeFilter: [
      {value: 'FreightCharge', text: 'Freight Charge'},
      {value: 'FuelSurcharge', text: 'Fuel Surcharge'},
      {value: 'GeneralSurcharge', text: 'General Surcharge'},
      {value: 'Unknown', text: 'Unknown'}
    ],
    fields: [
      { key: 'source', label: 'Source', sortable: false },
      { key: 'country', label: 'Country', sortable: false },
      { key: 'status', label: 'Status', sortable: false },
      { key: 'chargeClassCode', label: 'Class Code', sortable: false },
      { key: 'chargeDescriptionCode', label: 'Description Code', sortable: false },
      { key: 'chargeDescription', label: 'Description', sortable: false },
      { key: 'type', label: 'Type', sortable: false },
      { key: 'createDateTimeUtc', label: 'Created', sortable: false },
      { key: 'actions', label: 'Actions', sortable: false }
    ]
  }),
  mounted() {
    let queryHas = this.$route.query;
    if (queryHas) {
      this.resetFormFilters();
      this.formFilters = Object.assign(this.itemsFilters, queryHas);
      this.itemsFilters = Object.assign(this.itemsFilters, queryHas);
    } else {
      this.resetFormFilters();
    }
    this.loadItems();
  },

  methods: {
  }
};
</script>